import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Search,
  Navigation,
  LocateFixed,
} from "lucide-react";
import {
  getLocationWithCache,
  requestLocationOnUserAction,
  getAMapKey,
  getAMapWebServiceKey,
  getAMapSafeKey,
  getCachedLocation,
  searchPoiTips,
  DEFAULT_CITY,
} from "@/utils/location";
import type { PoiTip } from "@/utils/location";
import { getAllCity, type CityItem } from "@/api/city";

// 热门城市
const HOT_CITIES = [
  "成都",
  "北京",
  "上海",
  "广州",
  "深圳",
  "杭州",
  "重庆",
  "西安",
];

// 最近使用（从 localStorage 读取）
const getRecentCities = (): string[] => {
  try {
    const recent = localStorage.getItem("recent_cities");
    return recent ? JSON.parse(recent) : [];
  } catch {
    return [];
  }
};

const saveRecentCity = (city: string) => {
  try {
    const recent = getRecentCities();
    const newRecent = [city, ...recent.filter((c) => c !== city)].slice(0, 5);
    localStorage.setItem("recent_cities", JSON.stringify(newRecent));
  } catch {
    // ignore
  }
};

/**
 * 从地址中提取【最精确】的 "xx市"（取最后一个匹配，兼容县级市）
 *
 * 示例:
 *   "四川省南充市阆中市" → "阆中市"   （县级市优先）
 *   "四川省成都市"       → "成都市"
 *   "广东省广州市天河区" → "广州市"
 *   "北京市"             → "北京市"
 */
const extractMostSpecificCity = (address: string): string => {
  if (!address) return "";
  // 全局匹配所有 "xx市"，取最后一个（最精确、层级最低）
  const matches = address.match(/[\u4e00-\u9fa5]+?市/g);
  if (matches && matches.length > 0) {
    return matches[matches.length - 1];
  }
  return "";
};

/**
 * 从 district 中提取乡镇/街道级别信息
 * 示例: "四川省成都市武侯区" → "武侯区", "广东省深圳市南山区" → "南山区"
 */
const extractTownshipFromDistrict = (district: string): string => {
  if (!district) return "";
  // 匹配 "市" 后面的区/县/镇/街道部分
  const match = district.match(/(?:市)([^省]+?)(?:$)/);
  if (match && match[1]) {
    return match[1];
  }
  return "";
};

// 搜索结果类型（合并「城市列表」与「高德 POI 联想」两种来源）
interface PoiItem {
  id: string;
  name: string;
  district: string;
  adcode: string;
  location: string;
  address: string;
  /** 来源：city=本地城市列表，poi=高德输入提示 */
  source: "city" | "poi";
}

export default function LocationSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || "/cars";

  // 接收位置选择结果（从 location-select 返回时使用）
  const {
    selectedCity: stateSelected,
    selectedLat: stateSelectedLat,
    selectedLng: stateSelectedLng,
    selectedTownship: stateSelectedTownship,
  } = (location.state as any) || {};

  const [searchKeyword, setSearchKeyword] = useState("");
  // 缓存全部城市列表（只请求一次API）
  const [allCities, setAllCities] = useState<CityItem[]>([]);
  const [searchResults, setSearchResults] = useState<PoiItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string>("定位中...");
  const [isLocating, setIsLocating] = useState(false);
  const [isSelecting, setIsSelecting] = useState<string | null>(null);
  const [recentCities, setRecentCities] = useState<string[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [selectedMapLocation, setSelectedMapLocation] = useState<{
    name: string;
    city: string;
    township?: string;
    location?: string;
  } | null>(null);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const amapRef = useRef<any>(null);
  const citiesLoaded = useRef(false); // 防止重复请求

  // 初始化最近使用城市
  useEffect(() => {
    setRecentCities(getRecentCities());
  }, []);

  // 获取当前位置
  useEffect(() => {
    const loadLocation = async () => {
      const loc = await getLocationWithCache();
      setCurrentLocation(loc.township || loc.city || "成都");
    };
    loadLocation();
  }, []);

  // 首次加载时获取全部城市并缓存
  const loadAllCities = useCallback(async () => {
    if (citiesLoaded.current) return;
    try {
      setIsSearching(true);
      const res = await getAllCity();
      if (res.data.code === 200 && res.data.data?.length) {
        setAllCities(res.data.data);
        citiesLoaded.current = true;
      }
    } catch (error) {
      console.error("获取城市列表失败:", error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // 前端纯过滤：从已缓存的城市列表中匹配关键词（无网络请求）
  const filterCities = useCallback((keyword: string): PoiItem[] => {
    if (!keyword.trim() || allCities.length === 0) return [];

    return allCities
      .filter((item) => item.name.includes(keyword))
      .map((item: CityItem) => ({
        id: `city_${item.city_id}`,
        name: item.name,
        district: item.merger_name || "",
        adcode: String(item.city_id),
        location: item.lng && item.lat ? `${item.lng},${item.lat}` : "",
        address: item.name,
        source: "city" as const,
      }));
  }, [allCities]);

  /**
   * 综合搜索：本地城市列表 + 高德 POI 联想
   *
   * - 城市名（如「成都」）→ 命中本地城市列表，可直接切城市
   * - POI 关键字（如「图书馆」）→ 走高德输入提示，返回具体地点
   * 两类结果合并展示，城市在前、POI 在后。
   */
  const doSearch = useCallback(
    async (keyword: string) => {
      const kw = keyword.trim();
      if (!kw) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        // 本地城市匹配（同步，立即出结果）
        const cityHits = filterCities(kw);

        // 高德 POI 联想（异步，限定当前城市提高准确度）
        const tips: PoiTip[] = await searchPoiTips(
          kw,
          currentLocation && currentLocation !== "定位中..." ? currentLocation : undefined,
        );

        const poiHits: PoiItem[] = tips.map((t) => ({
          id: `poi_${t.id}`,
          name: t.name,
          district: t.district,
          adcode: t.adcode || "",
          location: t.location,
          address: t.district || t.name,
          source: "poi" as const,
        }));

        // 合并去重（同名同地址只保留一条）
        const seen = new Set<string>();
        const merged: PoiItem[] = [];
        for (const item of [...cityHits, ...poiHits]) {
          const k = `${item.name}|${item.district}`;
          if (seen.has(k)) continue;
          seen.add(k);
          merged.push(item);
        }
        setSearchResults(merged);
      } catch (e) {
        console.error("[location-select] 搜索失败:", e);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [filterCities, currentLocation],
  );

  // 防抖搜索：400ms 内持续输入不发出请求，首次输入触发加载城市列表
  useEffect(() => {
    // 首次输入时确保城市列表已加载（用于城市名匹配）
    if (!citiesLoaded.current) {
      loadAllCities();
    }

    const timer = setTimeout(() => {
      doSearch(searchKeyword);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchKeyword, doSearch, loadAllCities]);

  // 通过地理编码API获取经纬度
  const geocodeAddress = async (
    address: string,
  ): Promise<{ lng: string; lat: string } | null> => {
    const key = getAMapWebServiceKey();
    if (!key) return null;
    try {
      const geoUrl = `https://restapi.amap.com/v3/geocode/geo?key=${key}&address=${encodeURIComponent(address)}`;
      const res = await fetch(geoUrl);
      const data = await res.json();
      if (data.status === "1" && data.geocodes?.length > 0) {
        const loc = data.geocodes[0].location;
        if (loc) {
          const [lng, lat] = loc.split(",");
          return { lng, lat };
        }
      }
    } catch (e) {
      console.error("[地理编码] 失败:", e);
    }
    return null;
  };

  /**
   * 选中的地点（搜索下拉里点选后展示在地图上，用户确认后再返回）
   * 与地图点击选点的 selectedMapLocation 复用同一套 UI
   */
  const [selectedPoi, setSelectedPoi] = useState<{
    name: string;
    city: string;
    township?: string;
    location?: string;
    address?: string;
  } | null>(null);
  // ★ ref 镜像：地图初始化 effect 只依赖 showMap，需要读最新选中值
  const selectedPoiRef = useRef(selectedPoi);
  selectedPoiRef.current = selectedPoi;

  // 选择位置（支持自动地理编码补全经纬度）
  const handleSelectLocation = async (
    name: string,
    city?: string,
    coord?: string, // 经纬度字符串 "lng,lat"
    district?: string, // 搜索结果中的district字段，用于提取精确城市名
    rawCityFromMap?: string, // 地图选中时直接传入的城市名
    rawTownship?: string, // 乡镇/街道信息
    source: "city_search" | "map_select" | "recent" | "location" = "city_search",
  ) => {
    setIsSelecting(name);

    // 提取精确城市名，优先级：地图选中 > district解析 > 原始city > 原始name
    let finalCity = "";
    if (rawCityFromMap) {
      finalCity = rawCityFromMap;
    } else if (district) {
      finalCity = name;
    }
    if (!finalCity) {
      finalCity = city || name;
    }

    saveRecentCity(finalCity);

    // 解析经纬度，格式为 "经度,纬度"
    let latitude = "";
    let longitude = "";
    if (coord && typeof coord === "string") {
      const parts = coord.split(",");
      if (parts.length === 2) {
        longitude = parts[0];
        latitude = parts[1];
      }
    }

    // 如果没有经纬度，通过地理编码API自动获取
    if (!latitude || !longitude) {
      const result =
        (await geocodeAddress(name)) || (await geocodeAddress(city || name));
      if (result) {
        longitude = result.lng;
        latitude = result.lat;
        console.log("[地理编码] 自动补全经纬度:", name, latitude, longitude);
      } else {
        console.warn("[地理编码] 未获取到经纬度，使用空值:", name);
      }
    }

    // 提取 township
    const finalTownship =
      rawTownship || extractTownshipFromDistrict(district) || "";

    setIsSelecting(null);

    console.log(
      "[位置选择] 返回数据:",
      finalCity,
      latitude,
      longitude,
      "township:",
      finalTownship,
    );

    // 构建返回的 state：合并原始调用参数 + 选择的结果
    const originalState = (location.state as Record<string, any>) || {};
    const { from: _from, ...restOriginalState } = originalState; // 剔除 from，保留 staffId、itemId 等

    navigate(from, {
      state: {
        ...restOriginalState, // 透传原始参数（如 staffId、itemId）
        selectedCity: finalCity,
        selectedLat: latitude,
        selectedLng: longitude,
        selectedTownship: finalTownship,
        source,
      },
      replace: true,
    });
  };

  /**
   * 从搜索下拉中选中一个地点 → 展示在下方地图中（不直接返回）
   *
   * 流程：收起下拉 → 打开地图 → 地图标记移动到该点 → 显示确认按钮
   */
  const handlePickPoi = async (item: PoiItem) => {
    // 收起下拉框
    setSearchKeyword("");

    let { location } = item;
    // POI 可能没有经纬度（如行政区划），用地理编码补全
    if (!location) {
      const cityForGeo = item.city || item.name;
      const geo = await geocodeAddress(item.name) || await geocodeAddress(cityForGeo);
      if (geo) location = `${geo.lng},${geo.lat}`;
    }

    const [lng, lat] =
      location && location.includes(",") ? location.split(",") : ["", ""];

    // ★ 城市名优先级：
    //   ① 从详细地址解析【最精确】的城市名（如「四川省南充市阆中市…」→「阆中市」，
    //      取最后一个「xx市」以兼容县级市）
    //   ② POI 自带 city 字段（通常是地级市，精度较低，放后面）
    //   ③ 兜底：用定位城市
    //   ⚠️ 不能直接用 currentLocation，否则选中外地 POI 会错误显示定位城市
    const address = item.district || item.address || "";
    const cityFromAddress = extractMostSpecificCity(address);
    const finalCityName = cityFromAddress || item.city || currentLocation;

    const picked = {
      name: item.name,
      city: finalCityName,
      township: item.district || "",
      location: lng && lat ? `${lng},${lat}` : undefined,
      address: address || item.name,
    };

    setSelectedPoi(picked);
    setSelectedMapLocation(null); // 清掉地图点击选点，避免两个确认区同时出现
    setShowMap(true); // ★ 打开下方地图
  };

  // 地图就绪 / POI 变化时，把地图中心与标记移动到选中地点
  useEffect(() => {
    const map = mapInstanceRef.current;
    const marker = markerRef.current;
    const AMap = amapRef.current;
    if (!map || !marker || !AMap) return;
    if (!selectedPoi?.location) return;

    const [lngStr, latStr] = selectedPoi.location.split(",");
    const lng = Number(lngStr);
    const lat = Number(latStr);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

    const pos = new AMap.LngLat(lng, lat);
    marker.setPosition(pos);
    map.setCenter(pos);
    map.setZoom(16);
  }, [selectedPoi]);

  // 重新定位
  const handleRelocate = async () => {
    setIsLocating(true);
    const result = await requestLocationOnUserAction();
    if (result.success && result.location) {
      const loc = result.location;
      const cityName = loc.city || loc.district || "成都";
      setCurrentLocation(cityName);

      const lat = String(loc.latitude || "");
      const lng = String(loc.longitude || "");

      handleSelectLocation(
        cityName,
        cityName,
        lng && lat ? `${lng},${lat}` : "",
        undefined,
        undefined,
        loc.township || loc.district || cityName,
        "location",
      );
    } else {
      alert(`定位失败: ${result.error || "请检查定位权限"}`);
    }
    setIsLocating(false);
  };

  // 初始化高德地图
  useEffect(() => {
    if (!showMap || !mapRef.current) return;

    const initMap = () => {
      const AMap = (window as any).AMap;
      if (!AMap) {
        console.error("高德地图 SDK 未加载");
        return;
      }

      amapRef.current = AMap;

      // 中心点优先级：搜索选中的 POI > 定位缓存 > 默认城市（成都）
      let centerLng = 0;
      let centerLat = 0;
      let centerSrc = "";

      const poiLoc = selectedPoiRef.current?.location;
      if (poiLoc && poiLoc.includes(",")) {
        const [lng, lat] = poiLoc.split(",").map(Number);
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          centerLng = lng;
          centerLat = lat;
          centerSrc = "搜索选中地点";
        }
      }

      if (!centerSrc) {
        const cached = getCachedLocation();
        centerLng = cached ? cached.longitude : DEFAULT_CITY.longitude;
        centerLat = cached ? cached.latitude : DEFAULT_CITY.latitude;
        centerSrc = cached ? "定位缓存" : "默认城市";
      }

      console.log("地图中心点:", centerLng, centerLat, "来源:", centerSrc);

      // 创建地图
      const map = new AMap.Map(mapRef.current, {
        zoom: centerSrc === "搜索选中地点" ? 16 : 12,
        center: [centerLng, centerLat],
      });

      mapInstanceRef.current = map;

      // 添加标记
      const marker = new AMap.Marker({
        position: [centerLng, centerLat],
        draggable: true,
      });

      marker.on("dragend", () => {
        const position = marker.getPosition();
        console.log("选择位置:", position);
      });

      map.add(marker);
      markerRef.current = marker;

      // 点击地图选择位置
      map.on("click", (e: any) => {
        const lng = e.lnglat.lng; // 经度
        const lat = e.lnglat.lat; // 纬度
        console.log("选择位置:", lng, lat);
        marker.setPosition(e.lnglat);
        // 逆地理编码获取地址
        const geocoder = new AMap.Geocoder({
          radius: 1000,
          extensions: "all",
        });
        geocoder.getAddress(e.lnglat, (status: string, result: any) => {
          console.log("逆地理编码结果:", status, result);
          if (status === "complete" && result.regeocode) {
            const address = result.regeocode.formattedAddress;
            const ac = result.regeocode.addressComponent;
            // ★ 优先从完整地址解析最精确的城市名（兼容县级市），
            //   ac.city 为空或只到地级市时作为兜底
            const city =
              extractMostSpecificCity(address) ||
              (typeof ac.city === "string" && ac.city) ||
              "";
            const township = ac.township || "";
            console.log("选择地址:", address, city, "township:", township);
            // 保存选中的位置，等待用户确认（显示详细地址）
            setSelectedMapLocation({
              name: address,
              city: city || "未知",
              township,
              location: `${lng},${lat}`,
            });
          }
        });
      });

      // 定位到当前位置
      map.plugin("AMap.Geolocation", () => {
        const geolocation = new AMap.Geolocation({
          enableHighAccuracy: true,
          timeout: 10000,
          buttonOffset: new AMap.Pixel(10, 20),
          zoomToAccuracy: true,
        });
        map.addControl(geolocation);
      });
    };

    // 检查高德地图 SDK 是否已加载
    if ((window as any).AMap) {
      initMap();
    } else {
      // 加载高德地图 SDK
      const key = getAMapKey();
      const safeKey = getAMapSafeKey();

      // 先加载安全密钥配置
      if (safeKey) {
        (window as any)._AMapSecurityConfig = {
          securityJsCode: safeKey,
        };
      }

      const script = document.createElement("script");
      script.src = `https://webapi.amap.com/maps?v=2.0&key=${key}&plugin=AMap.Geocoder,AMap.Geolocation`;
      script.onload = initMap;
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
      }
    };
  }, [showMap]);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Header */}
      <div className="bg-white sticky top-0 z-40">
        <div className="px-4 pb-3 flex items-center gap-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full"
            style={{ background: "#F3F4F6" }}
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索城市、地点"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>
      </div>

      {/* 搜索结果（下拉列表：城市 + POI 联想） */}
      {searchKeyword && (
        <div
          className="bg-white absolute left-0 right-0 z-30 overflow-y-auto"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 100px)",
            maxHeight: "60vh",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderTop: "1px solid #F3F4F6",
          }}
        >
          {isSearching && searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              搜索中...
            </div>
          ) : searchResults.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {searchResults.map((item) => {
                const isItemLoading = isSelecting === item.name;
                const isCity = item.source === "city";
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      // 城市项 → 直接切换城市返回；POI 项 → 显示在地图上待确认
                      if (item.source === "city") {
                        handleSelectLocation(
                          item.name,
                          item.name,
                          item.location,
                          item.district,
                          undefined,
                          item.name,
                          "city_search",
                        );
                      } else {
                        handlePickPoi(item);
                      }
                    }}
                    disabled={!!isSelecting}
                    className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60"
                  >
                    <span
                      className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5"
                      style={{ background: isCity ? "#EFF6FF" : "#FFF7ED" }}
                    >
                      {isCity ? (
                        <MapPin className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Search className="w-4 h-4 text-amber-500" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {isItemLoading ? "定位中..." : item.name}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5 truncate">
                        {item.district || item.address || (isCity ? "城市" : "")}
                      </div>
                    </div>
                    {isCity && (
                      <span className="text-xs text-blue-500 flex-shrink-0 mt-1">
                        切换城市
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              未找到相关地点
            </div>
          )}
        </div>
      )}

      {/* 当前位置 */}
      {!searchKeyword && (
        <>
          <div className="bg-white mt-2">
            <button
              onClick={handleRelocate}
              disabled={isLocating}
              className="w-full px-4 py-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <LocateFixed className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-gray-900">
                  {isLocating ? "定位中..." : "当前位置"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {currentLocation}
                </div>
              </div>
              <Navigation className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* 地图选点入口 */}
          <div className="bg-white mt-2 px-4 py-3">
            <button
              onClick={() => setShowMap(!showMap)}
              className="w-full py-3 bg-amber-500 text-white rounded-xl text-sm font-medium"
            >
              {showMap ? "关闭地图选点" : "在地图上选择位置"}
            </button>
          </div>

          {/* 地图容器 */}
          {showMap && (
            <div className="bg-white mt-2 p-4">
              <div
                ref={mapRef}
                className="w-full h-80 rounded-xl"
                style={{ background: "#f0f0f0" }}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                拖动标记或点击地图选择位置
              </p>

              {/* ★ 搜索选中地点 → 地图确认区 */}
              {selectedPoi && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    已选择:{" "}
                    <span className="font-medium text-amber-700">
                      {selectedPoi.name}
                    </span>
                  </p>
                  {selectedPoi.address && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {selectedPoi.address}
                    </p>
                  )}
                  <button
                    onClick={() =>
                      handleSelectLocation(
                        selectedPoi.name,
                        selectedPoi.city,
                        selectedPoi.location,
                        undefined,
                        selectedPoi.city,
                        selectedPoi.township,
                        "map_select",
                      )
                    }
                    className="w-full mt-2 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium"
                  >
                    确认选择
                  </button>
                </div>
              )}
              {selectedMapLocation && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    已选择:{" "}
                    <span className="font-medium text-amber-700">
                      {selectedMapLocation.name}
                    </span>
                  </p>
                  <button
                    onClick={() =>
                      handleSelectLocation(
                        selectedMapLocation.name,
                        selectedMapLocation.city,
                        selectedMapLocation.location,
                        undefined, // district（地图选中不需要）
                        selectedMapLocation.city, // rawCityFromMap: 直接使用逆地理编码的城市名
                        selectedMapLocation.township, // rawTownship: 逆地理编码的乡镇信息
                        "map_select",
                      )
                    }
                    className="w-full mt-2 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium"
                  >
                    确认选择
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 最近使用 */}
          {recentCities.length > 0 && (
            <div className="bg-white mt-2 px-4 py-4">
              <div className="text-sm font-medium text-gray-900 mb-3">
                最近使用
              </div>
              <div className="flex flex-wrap gap-2">
                {recentCities.map((city) => (
                  <button
                    key={city}
                    onClick={() => handleSelectLocation(city, undefined, undefined, undefined, undefined, undefined, "recent")}
                    className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-700"
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 热门城市 */}
          {/* <div className="bg-white mt-2 px-4 py-4"> */}
          {/*   <div className="text-sm font-medium text-gray-900 mb-3"> */}
          {/*     热门城市 */}
          {/*   </div> */}
          {/*   <div className="grid grid-cols-4 gap-3"> */}
          {/*     {HOT_CITIES.map((city) => ( */}
          {/*       <button */}
          {/*         key={city} */}
          {/*         onClick={() => handleSelectLocation(city)} */}
          {/*         className="py-2 bg-gray-100 rounded-lg text-sm text-gray-700" */}
          {/*       > */}
          {/*         {city} */}
          {/*       </button> */}
          {/*     ))} */}
          {/*   </div> */}
          {/* </div> */}
        </>
      )}
    </div>
  );
}
